import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Phone, X } from 'lucide-react';
import { useCallQueue } from '../../contexts/CallQueueContext';
import CallQueueItem from './CallQueueItem';
import CallResultModal from './CallResultModal';
import './CallQueuePanel.css';

const CallQueuePanel: React.FC = () => {
  const {
    queue,
    selectedCallId,
    isExpanded,
    selectCall,
    collapsePanel,
  } = useCallQueue();

  const [isClosing, setIsClosing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Текущая выбранная карточка
  const selectedCall = useMemo(() => {
    if (!selectedCallId) return null;
    return queue.find((c) => c.callId === selectedCallId) ?? null;
  }, [queue, selectedCallId]);

  // Закрытие панели с анимацией
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      collapsePanel();
    }, 200);
  }, [collapsePanel]);

  // Клик по карточке в списке - открываем модалку
  const handleItemClick = useCallback(
    (callId: string) => {
      selectCall(callId);
      setIsModalOpen(true);
    },
    [selectCall]
  );

  // Закрытие модалки
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    selectCall(null);
  }, [selectCall]);

  // Закрытие по Escape
  useEffect(() => {
    if (!isExpanded) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded, handleClose]);

  // Закрытие по клику вне панели
  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (e: MouseEvent) => {
      // Не закрываем панель, если открыта модалка
      if (isModalOpen) return;
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };

    // Задержка, чтобы не закрыть сразу после открытия
    const timeout = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, isModalOpen, handleClose]);

  // isMac для отображения shortcut
  const isMac = useMemo(() => {
    return typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
  }, []);

  if (!isExpanded) {
    return null;
  }

  const panelClasses = ['call-queue-panel', isClosing && 'call-queue-panel--closing']
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {/* Overlay */}
      <div className="call-queue-overlay" onClick={handleClose} />

      {/* Panel */}
      <div ref={panelRef} className={panelClasses} role="dialog" aria-modal="true">
        {/* Header */}
        <div className="call-queue-panel__header">
          <div className="call-queue-panel__title">
            <Phone size={18} />
            <span>Очередь звонков</span>
            {queue.length > 0 && (
              <span className="call-queue-panel__count">{queue.length}</span>
            )}
          </div>
          <button
            type="button"
            className="call-queue-panel__close"
            onClick={handleClose}
            aria-label="Закрыть панель"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="call-queue-panel__body">
          {queue.length === 0 ? (
            <div className="call-queue-panel__empty">
              <Phone className="call-queue-panel__empty-icon" />
              <div className="call-queue-panel__empty-text">
                Нет звонков в очереди
              </div>
            </div>
          ) : (
            queue.map((call) => (
              <CallQueueItem
                key={call.callId}
                call={call}
                isSelected={call.callId === selectedCallId}
                onClick={() => handleItemClick(call.callId)}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="call-queue-panel__footer">
          <div className="call-queue-panel__shortcut">
            <kbd>{isMac ? '⌘' : 'Ctrl'}</kbd>
            <span>+</span>
            <kbd>K</kbd>
            <span>— быстрый доступ</span>
          </div>
        </div>
      </div>

      {/* Modal */}
      {selectedCall && (
        <CallResultModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          call={selectedCall}
        />
      )}
    </>
  );
};

export default CallQueuePanel;
